import { useParams, Navigate, Link } from 'react-router-dom';
import { VanguardNav } from '../components/vanguard/VanguardNav';
import { LandingFooter } from '../components/landing/LandingFooter';
import { PageTransition } from '../components/common/PageTransition';
import { usePageMeta } from '../lib/usePageMeta';
import { trackConversion } from '../lib/analytics';
import { useJsonLd } from '../lib/useJsonLd';
import { artigosData, type Bloco } from '../data/artigos';

const SITE = 'https://paccesadvogados.com.br';
const WHATSAPP = '5511969241378';

const dataPorExtenso = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

const BlocoRender = ({ bloco }: { bloco: Bloco }) => {
    switch (bloco.tipo) {
        case 'h2':
            return (
                <h2 className="font-serif text-2xl md:text-3xl leading-snug text-pacces-ebony dark:text-white mt-14 mb-5">
                    {bloco.texto}
                </h2>
            );

        case 'p':
            return (
                <p className="font-vanguard-blog text-[1.0625rem] md:text-lg leading-[1.85] text-stone-700 dark:text-stone-300 mb-6">
                    {bloco.texto}
                </p>
            );

        case 'lista':
            return (
                <ul className="mb-8 mt-2 space-y-4">
                    {bloco.itens.map((item) => (
                        <li key={item} className="flex gap-4">
                            <span
                                aria-hidden
                                className="mt-[0.7rem] h-px w-4 shrink-0 bg-vanguard-primary"
                            />
                            <span className="font-vanguard-blog text-[1.0625rem] md:text-lg leading-[1.75] text-stone-700 dark:text-stone-300">
                                {item}
                            </span>
                        </li>
                    ))}
                </ul>
            );

        case 'indice':
            return (
                <ul className="mb-10 mt-2 divide-y divide-stone-200 dark:divide-stone-800 border-y border-stone-200 dark:border-stone-800">
                    {bloco.itens.map((item) => (
                        <li key={item.slug}>
                            <Link
                                to={`/conteudo/${item.slug}`}
                                className="group flex items-baseline gap-4 py-5 transition-colors"
                            >
                                <span className="font-vanguard-blog text-[1.0625rem] md:text-lg leading-[1.7] text-stone-700 dark:text-stone-300 group-hover:text-vanguard-accent dark:group-hover:text-vanguard-primary">
                                    {item.texto}
                                </span>
                                <span
                                    aria-hidden
                                    className="ml-auto shrink-0 text-vanguard-accent dark:text-vanguard-primary transition-transform group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            );

        case 'destaque':
            return (
                <blockquote className="my-12 border-l-2 border-vanguard-primary pl-6 md:pl-8">
                    <p className="font-serif text-xl md:text-2xl leading-relaxed text-pacces-ebony dark:text-white">
                        {bloco.texto}
                    </p>
                </blockquote>
            );

        case 'faq':
            return (
                <div className="my-12 border-t border-stone-200 dark:border-stone-800">
                    {bloco.itens.map((f) => (
                        <div
                            key={f.pergunta}
                            className="border-b border-stone-200 dark:border-stone-800 py-7"
                        >
                            <h3 className="font-serif text-lg md:text-xl text-pacces-ebony dark:text-white mb-3">
                                {f.pergunta}
                            </h3>
                            <p className="font-vanguard-blog text-[1.0625rem] leading-[1.8] text-stone-700 dark:text-stone-300">
                                {f.resposta}
                            </p>
                        </div>
                    ))}
                </div>
            );
    }
};

export const ArtigoPage = () => {
    const { slug } = useParams();
    const artigo = slug ? artigosData[slug] : null;

    const url = `${SITE}/conteudo/${slug}`;

    usePageMeta({
        title: artigo ? `${artigo.titulo} | Pacces Advogados` : 'Conteúdo | Pacces Advogados',
        description: artigo?.resumo,
        // TEMPORARIO — retirar quando o Dr. Luke validar os textos.
        // O blog sobe junto no bundle (o build e unico), mas nao deve ser
        // indexado antes da aprovacao. Ao liberar: apagar esta linha E
        // devolver as URLs ao public/sitemap.xml.
        robots: 'noindex, follow',
        canonical: url,
        image: artigo ? `${SITE}${artigo.imagem}` : undefined,
    });

    // Article + FAQPage. O FAQPage só entra quando o artigo tem perguntas —
    // declarar um vazio é pior que não declarar.
    const perguntas =
        artigo?.corpo.flatMap((b) => (b.tipo === 'faq' ? b.itens : [])) ?? [];

    useJsonLd(
        artigo
            ? [
                  {
                      '@context': 'https://schema.org',
                      '@type': 'Article',
                      headline: artigo.titulo,
                      description: artigo.resumo,
                      image: `${SITE}${artigo.imagem}`,
                      datePublished: artigo.atualizadoEm,
                      dateModified: artigo.atualizadoEm,
                      inLanguage: 'pt-BR',
                      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
                      author: { '@type': 'Organization', name: 'Pacces Advogados', url: SITE },
                      publisher: { '@type': 'Organization', name: 'Pacces Advogados', url: SITE },
                  },
                  ...(perguntas.length
                      ? [
                            {
                                '@context': 'https://schema.org',
                                '@type': 'FAQPage',
                                mainEntity: perguntas.map((f) => ({
                                    '@type': 'Question',
                                    name: f.pergunta,
                                    acceptedAnswer: { '@type': 'Answer', text: f.resposta },
                                })),
                            },
                        ]
                      : []),
              ]
            : [],
        `artigo-${slug}`,
    );

    if (!artigo) return <Navigate to="/conteudo" replace />;

    const whatsappHref = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
        `Olá! Li o artigo "${artigo.titulo}" no site e gostaria de entender se o meu caso se enquadra.`,
    )}`;

    const relacionados = artigo.relacionados
        .map((s) => artigosData[s])
        .filter(Boolean);

    return (
        <PageTransition>
            {/* `[&_p]:text-left [&_li]:text-left`: o index.css justifica p e li em
                @layer base. Justificado sem hifenização abre rios no meio do
                parágrafo — em coluna de leitura isso cansa. */}
            <div className="bg-white dark:bg-vanguard-background-dark text-stone-900 dark:text-stone-300 font-vanguard-sans transition-colors duration-300 [&_p]:text-left [&_li]:text-left">
                <VanguardNav />

                <article>
                    <header className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 md:pt-40 pb-10">
                        <Link
                            to="/conteudo"
                            className="text-[11px] tracking-[0.28em] uppercase text-stone-500 dark:text-stone-500 hover:text-vanguard-accent dark:hover:text-vanguard-primary transition-colors"
                        >
                            ← Conteúdo
                        </Link>

                        <p className="mt-8 text-[11px] tracking-[0.3em] uppercase text-vanguard-accent dark:text-vanguard-primary font-medium">
                            {artigo.categoria}
                        </p>

                        <h1 className="mt-5 font-serif text-3xl md:text-5xl leading-[1.1] text-pacces-ebony dark:text-white">
                            {artigo.titulo}
                        </h1>

                        <p className="mt-6 font-vanguard-blog text-lg md:text-xl leading-relaxed text-stone-600 dark:text-stone-400">
                            {artigo.resumo}
                        </p>

                        <p className="mt-8 text-xs tracking-[0.14em] uppercase text-stone-500">
                            Atualizado em {dataPorExtenso(artigo.atualizadoEm)} · {artigo.leituraMin} min de leitura
                        </p>
                    </header>

                    <figure className="max-w-5xl mx-auto px-6 lg:px-8 mb-14 md:mb-20">
                        <img
                            src={artigo.imagem}
                            alt={artigo.imagemAlt}
                            className="w-full h-64 md:h-[30rem] object-cover rounded-md"
                        />
                    </figure>

                    <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-8">
                        {artigo.corpo.map((bloco, i) => (
                            <BlocoRender key={i} bloco={bloco} />
                        ))}
                    </div>

                    {/* CTA — sóbrio, sem promessa de resultado (OAB, Prov. 205/2021) */}
                    <aside className="max-w-3xl mx-auto px-6 lg:px-8 mt-6 mb-24">
                        <div className="border border-stone-200 dark:border-stone-800 rounded-md p-8 md:p-10 bg-pacces-ivory dark:bg-black/20">
                            <p className="text-[11px] tracking-[0.3em] uppercase text-vanguard-accent dark:text-vanguard-primary font-medium">
                                Próximo passo
                            </p>
                            <h2 className="mt-4 font-serif text-2xl md:text-3xl leading-snug text-pacces-ebony dark:text-white">
                                Quer saber se o seu caso se enquadra?
                            </h2>
                            <p className="mt-4 font-vanguard-blog text-base leading-relaxed text-stone-600 dark:text-stone-400">
                                Conte a situação do imóvel e os documentos que você já tem em casa.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-4">
                                {/* Direto para o WhatsApp, sem formulário. O leitor do blog
                                    chegou para entender, não para contratar — e os três campos
                                    obrigatórios do modal não são gravados em lugar nenhum, só
                                    colados no texto da própria mensagem. Atrito sem contrapartida.
                                    O modal segue nas LPs de anúncio, onde qualificar se paga. */}
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackConversion('sp', 'whatsapp')}
                                    className="px-7 py-4 bg-pacces-ebony dark:bg-vanguard-primary text-white dark:text-pacces-ebony text-xs tracking-[0.2em] uppercase font-semibold hover:opacity-90 transition-opacity"
                                >
                                    Falar no WhatsApp
                                </a>
                                <Link
                                    to={`/areas/${artigo.areaCta}`}
                                    className="px-7 py-4 border border-stone-300 dark:border-stone-700 text-xs tracking-[0.2em] uppercase font-semibold text-stone-700 dark:text-stone-300 hover:border-vanguard-accent dark:hover:border-vanguard-primary transition-colors"
                                >
                                    Ver a área de atuação
                                </Link>
                            </div>
                        </div>
                    </aside>

                    {relacionados.length > 0 && (
                        <section className="max-w-6xl mx-auto px-6 lg:px-8 pb-28">
                            <div className="h-px w-full bg-stone-200 dark:bg-stone-800 mb-12" />
                            <p className="text-[11px] tracking-[0.3em] uppercase text-vanguard-accent dark:text-vanguard-primary font-medium mb-10">
                                Continue lendo
                            </p>
                            <div className="grid sm:grid-cols-3 gap-x-10 gap-y-12">
                                {relacionados.map((r) => (
                                    <Link key={r.slug} to={`/conteudo/${r.slug}`} className="group block">
                                        <div className="overflow-hidden rounded-md">
                                            <img
                                                src={r.imagem}
                                                alt={r.imagemAlt}
                                                className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                                loading="lazy"
                                            />
                                        </div>
                                        <h3 className="mt-4 font-serif text-lg leading-snug text-pacces-ebony dark:text-white group-hover:text-vanguard-accent dark:group-hover:text-vanguard-primary transition-colors">
                                            {r.titulo}
                                        </h3>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                </article>

                <LandingFooter />
            </div>
        </PageTransition>
    );
};
